import { motion } from 'framer-motion'

interface LogoItem {
  key: string
  imageSrc: string
  name: string
}

interface ScrollingLogosProps {
  title: string
  subtitle: string
  logos: LogoItem[]
  variant?: 'light' | 'dark'
}

const ITEM_WIDTH = 180
const GAP = 32

export default function ScrollingLogos({ title, subtitle, logos, variant = 'light' }: ScrollingLogosProps) {
  const rowDistance = (ITEM_WIDTH + GAP) * logos.length
  const row1 = [...logos, ...logos]
  const row2 = [...logos].reverse()
  const row2Doubled = [...row2, ...row2]
  const isDark = variant === 'dark'
  const sectionBg = isDark ? 'bg-brand-dark' : 'bg-white'
  const headingColor = isDark ? 'text-white' : 'text-brand-dark'
  const subtitleColor = isDark ? 'text-white/80' : 'text-brand-text'
  const tileBg = isDark ? 'bg-white/5 border-white/10 hover:bg-white' : 'bg-gray-50 border-gray-100 hover:bg-white'
  const fadeFrom = isDark ? 'from-brand-dark' : 'from-white'
  const fadeFromL = isDark ? 'from-brand-dark' : 'from-white'

  return (
    <section className={`py-12 md:py-16 ${sectionBg} relative overflow-hidden border-t-4 border-brand-red`}>
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative">
        <div className="text-center mb-12">
          <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-6 ${headingColor}`}>
            {title}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-gray-400 to-brand-red mx-auto mb-8" />
          <p className={`text-xl max-w-3xl mx-auto leading-relaxed mb-12 ${subtitleColor}`}>
            {subtitle}
          </p>
        </div>

        <div className="relative space-y-4">
          {/* Row 1 - scrolls right to left */}
          <div className="relative overflow-hidden py-4">
            <div className={`absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r ${fadeFromL} to-transparent z-10 pointer-events-none`} />
            <div className={`absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l ${fadeFrom} to-transparent z-10 pointer-events-none`} />

            <motion.div
              className="flex gap-8"
              style={{ width: 'max-content' }}
              animate={{ x: [0, -rowDistance] }}
              transition={{
                x: { duration: 40, repeat: Infinity, ease: 'linear' },
              }}
            >
              {row1.map((logo, i) => (
                <div
                  key={`r1-${logo.key}-${i}`}
                  className={`flex-shrink-0 flex items-center justify-center px-8 py-6 rounded-xl border grayscale hover:grayscale-0 hover:shadow-lg transition-all duration-300 cursor-pointer ${tileBg}`}
                  style={{ width: `${ITEM_WIDTH}px`, height: '100px' }}
                >
                  <img
                    src={logo.imageSrc}
                    alt={logo.name}
                    className="max-h-14 w-auto object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </motion.div>
          </div>

          {/* Row 2 - scrolls left to right */}
          <div className="relative overflow-hidden py-4">
            <div className={`absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r ${fadeFromL} to-transparent z-10 pointer-events-none`} />
            <div className={`absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l ${fadeFrom} to-transparent z-10 pointer-events-none`} />

            <motion.div
              className="flex gap-8"
              style={{ width: 'max-content' }}
              animate={{ x: [-rowDistance, 0] }}
              transition={{
                x: { duration: 50, repeat: Infinity, ease: 'linear' },
              }}
            >
              {row2Doubled.map((logo, i) => (
                <div
                  key={`r2-${logo.key}-${i}`}
                  className={`flex-shrink-0 flex items-center justify-center px-8 py-6 rounded-xl border grayscale hover:grayscale-0 hover:shadow-lg transition-all duration-300 cursor-pointer ${tileBg}`}
                  style={{ width: `${ITEM_WIDTH}px`, height: '100px' }}
                >
                  <img
                    src={logo.imageSrc}
                    alt={logo.name}
                    className="max-h-14 w-auto object-contain"
                    loading="lazy"
                  />
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}
